import { useState, useMemo, useCallback } from 'react';
import type { NavigationNode } from '../../utils/markdownNav';

interface NavigationTreeProps {
  nodes: NavigationNode[];
  activeId: string | null;
  onNavigate: (id: string) => void;
  idPrefix?: string; // Optional prefix for scoped IDs
  title?: string;
  showExpandAll?: boolean;
}

interface TreeNodeProps {
  node: NavigationNode;
  activeId: string | null;
  onNavigate: (id: string) => void;
  idPrefix?: string;
  level: number;
  isExpanded: boolean;
  onToggle: (id: string) => void;
  expandedIds: Set<string>;
}

function TreeNode({
  node,
  activeId,
  onNavigate,
  idPrefix,
  level,
  isExpanded,
  onToggle,
  expandedIds,
}: TreeNodeProps) {
  const hasChildren = node.children.length > 0;
  const fullId = idPrefix ? `${idPrefix}-${node.id}` : node.id;
  const isActive = activeId === fullId;

  // Check if any descendant is active
  const hasActiveDescendant = useMemo(() => {
    if (!activeId) return false;
    const checkDescendant = (n: NavigationNode): boolean => {
      const childFullId = idPrefix ? `${idPrefix}-${n.id}` : n.id;
      if (activeId === childFullId) return true;
      return n.children.some(checkDescendant);
    };
    return node.children.some(checkDescendant);
  }, [node, activeId, idPrefix]);

  return (
    <div className="relative">
      {/* Tree line connector (vertical line from parent) */}
      {level > 0 && (
        <div
          className="absolute border-l border-gray-600"
          style={{
            left: `${(level - 1) * 12 + 8}px`,
            top: 0,
            height: '50%',
          }}
        />
      )}

      {/* Horizontal connector to node */}
      {level > 0 && (
        <div
          className="absolute border-t border-gray-600"
          style={{
            left: `${(level - 1) * 12 + 8}px`,
            top: '50%',
            width: '8px',
          }}
        />
      )}

      <div
        data-nav-id={fullId}
        className={`flex items-center py-1 rounded cursor-pointer transition-colors relative ${
          isActive
            ? 'bg-blue-600/30 text-blue-300 font-medium'
            : hasActiveDescendant
            ? 'bg-blue-500/20 text-blue-400'
            : 'hover:bg-gray-700 text-gray-300'
        }`}
        style={{ paddingLeft: `${level * 12 + 4}px` }}
      >
        {/* Expand/collapse button */}
        {hasChildren ? (
          <button
            onClick={(e) => {
              e.stopPropagation();
              onToggle(fullId);
            }}
            className={`w-5 h-5 flex items-center justify-center mr-1 text-gray-500 hover:text-gray-300 flex-shrink-0 ${
              isActive ? 'text-blue-400' : ''
            }`}
            title={isExpanded ? 'Collapse' : 'Expand'}
          >
            <svg
              className={`w-3 h-3 transition-transform duration-200 ${
                isExpanded ? 'rotate-90' : ''
              }`}
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fillRule="evenodd"
                d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
                clipRule="evenodd"
              />
            </svg>
          </button>
        ) : (
          <span className="w-5 h-5 mr-1 flex-shrink-0" />
        )}

        {/* Node text */}
        <button
          onClick={() => onNavigate(fullId)}
          className={`flex-1 text-left text-xs truncate px-1 py-0.5 rounded ${
            isActive ? 'font-medium' : ''
          }`}
          title={node.text}
        >
          {node.text}
        </button>

        {/* Active indicator dot */}
        {isActive && (
          <span className="w-1.5 h-1.5 bg-blue-500 rounded-full mr-2 flex-shrink-0" />
        )}
      </div>

      {/* Children */}
      {hasChildren && isExpanded && (
        <div className="relative">
          {/* Vertical line connecting children */}
          <div
            className="absolute border-l border-gray-600"
            style={{
              left: `${level * 12 + 8}px`,
              top: 0,
              bottom: '50%',
            }}
          />

          {node.children.map((child) => (
            <TreeNode
              key={child.id}
              node={child}
              activeId={activeId}
              onNavigate={onNavigate}
              idPrefix={idPrefix}
              level={level + 1}
              isExpanded={expandedIds.has(idPrefix ? `${idPrefix}-${child.id}` : child.id)}
              onToggle={onToggle}
              expandedIds={expandedIds}
            />
          ))}
        </div>
      )}
    </div>
  );
}

export default function NavigationTree({
  nodes,
  activeId,
  onNavigate,
  idPrefix,
  title,
  showExpandAll = true,
}: NavigationTreeProps) {
  // Track expanded state for each node
  const [expandedIds, setExpandedIds] = useState<Set<string>>(() => {
    // Default: expand all nodes
    const ids = new Set<string>();
    const collectIds = (n: NavigationNode) => {
      const fullId = idPrefix ? `${idPrefix}-${n.id}` : n.id;
      if (n.children.length > 0) {
        ids.add(fullId);
        n.children.forEach(collectIds);
      }
    };
    nodes.forEach(collectIds);
    return ids;
  });

  const toggleExpanded = useCallback((id: string) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }, []);

  const expandAll = useCallback(() => {
    const ids = new Set<string>();
    const collectIds = (n: NavigationNode) => {
      const fullId = idPrefix ? `${idPrefix}-${n.id}` : n.id;
      if (n.children.length > 0) {
        ids.add(fullId);
        n.children.forEach(collectIds);
      }
    };
    nodes.forEach(collectIds);
    setExpandedIds(ids);
  }, [nodes, idPrefix]);

  const collapseAll = useCallback(() => {
    setExpandedIds(new Set());
  }, []);

  // Check if there are any expandable nodes
  const hasExpandableNodes = useMemo(() => {
    const checkExpandable = (n: NavigationNode): boolean => {
      if (n.children.length > 0) return true;
      return n.children.some(checkExpandable);
    };
    return nodes.some(checkExpandable);
  }, [nodes]);

  if (nodes.length === 0) {
    return null;
  }

  return (
    <div>
      {/* Header with expand/collapse buttons */}
      <div className="flex items-center justify-between mb-2">
        {title && <h4 className="text-xs font-medium text-gray-400 uppercase">{title}</h4>}
        {showExpandAll && hasExpandableNodes && (
          <div className="flex items-center space-x-1">
            <button
              onClick={expandAll}
              className="p-1 text-gray-500 hover:text-gray-300"
              title="Expand all"
            >
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5v-4m0 4h-4m4 0l-5-5"
                />
              </svg>
            </button>
            <button
              onClick={collapseAll}
              className="p-1 text-gray-500 hover:text-gray-300"
              title="Collapse all"
            >
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 9V4m0 0h5m-5 0l5 5M9 15v5m0 0h5m-5 0l5-5M15 9V4m0 0h-5m5 0l-5 5m5 11v-5m0 5h-5m5 0l-5-5"
                />
              </svg>
            </button>
          </div>
        )}
      </div>

      {/* Tree nodes */}
      <nav className="space-y-0.5">
        {nodes.map((node) => (
          <TreeNode
            key={node.id}
            node={node}
            activeId={activeId}
            onNavigate={onNavigate}
            idPrefix={idPrefix}
            level={0}
            isExpanded={expandedIds.has(idPrefix ? `${idPrefix}-${node.id}` : node.id)}
            onToggle={toggleExpanded}
            expandedIds={expandedIds}
          />
        ))}
      </nav>
    </div>
  );
}
